import { LoginError } from '../../../../../modules/login/services/login.service';
import type { LoginCommonCredentials} from '../../../../components/login-common/login-common.component';
import { LoginCommonMode } from '../../../../components/login-common/login-common.component';
import { TwoFactorError, TwoFactorService } from '../../services/two-factor.service';
import type { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { wrapAPICall } from '@ppl/utils/api.utils';
import type { Observable } from 'rxjs';
import { BehaviorSubject, EMPTY } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Component({
    selector: 'ppl-two-factor-unlock',
    templateUrl: './two-factor-unlock.component.html',
    styleUrls: ['./two-factor-unlock.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TwoFactorUnlockComponent {

  loading$ = new BehaviorSubject<boolean>(false);

  mode = LoginCommonMode.Email;

  unlockData: TwoFactorUnlockData;

  credentials$ = new BehaviorSubject<LoginCommonCredentials>({
    email: '',
    password: '',
    remember: false
  });

  error$ = new BehaviorSubject<{ error: LoginError, message: string }>(this.getInitErrorState());
  errorCode$: Observable<LoginError>;
  errorMessage$: Observable<string>;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private twoFactorService: TwoFactorService
  ) {
    this.errorCode$ = this.error$.pipe(map(error => error.error));
    this.errorMessage$ = this.error$.pipe(map(error => error.message));

    const redirectTo: string = this.activatedRoute.snapshot.data.redirectTo || '/';
    const navigation = this.router.getCurrentNavigation();
    const unlockState = navigation?.extras?.state as TwoFactorUnlockNavigateRouteState;
    if (unlockState) {
      const unlockData = unlockState.unlockData;
      this.unlockData = unlockData;
      if (unlockData.email) {
        this.credentials$.next({
          ...this.credentials$.value,
          email: unlockData.email
        });
      }

      if (unlockData.idp === TwoFactorUnlockIDP.Pipeliner && this.credentials$.value.email) {
        this.mode = LoginCommonMode.Password;
      }
    } else {
      this.router.navigate([redirectTo]);
    }
  }

  onEdit() {
    this.mode = LoginCommonMode.Email;
  }

  onSubmit(credentials: LoginCommonCredentials) {
    this.credentials$.next({
      ...credentials
    });

    if (this.mode === LoginCommonMode.Email && this.unlockData.idp === TwoFactorUnlockIDP.Pipeliner) {
      this.mode = LoginCommonMode.Password;
    } else {
      this.getSecurityToken(credentials).subscribe(response => {
        const { navigateBackUrl, navigateBackState } = this.unlockData;
        const state: TwoFactorUnlockNavigateBackRouteState = {
          unlockData: {
            security: {
              token: response.security_token,
              validUntil: response.valid_until
            },
            ...(navigateBackState ? { navigateBackState } : {})
          }
        };
        this.router.navigateByUrl(navigateBackUrl, { state });
      });
    }
  }

  getInitErrorState(): { error: LoginError, message: string } {
    return {
      error: LoginError.NO_ERROR,
      message: null
    };
  }

  getSecurityToken(credentials: LoginCommonCredentials) {
    this.error$.next(this.getInitErrorState());
    const { email, password } = credentials;

    return wrapAPICall(
      this.twoFactorService.getSecurityToken({ username: email, password }),
      this.loading$
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        const errorInfo = this.twoFactorService.getErrorInfo(error);
        const errorCode: LoginError = errorInfo.error === TwoFactorError.UNAUTHORIZED
          ? LoginError.ERROR_INCORRECT_LOGIN
          : LoginError.UNKNOWN_ERROR;
        this.error$.next({
          error: errorCode,
          message: errorInfo.message
        });
        return EMPTY;
      })
    );
  }
}

export enum TwoFactorUnlockIDP {
  Pipeliner = 'Pipeliner',
  Other = 'Other'
}

interface TwoFactorUnlockData<T = any> {
  idp: TwoFactorUnlockIDP;
  email?: string;
  navigateBackUrl: string;
  navigateBackState?: T;
}

export interface TwoFactorUnlockNavigateRouteState<T = any> {
  unlockData: TwoFactorUnlockData<T>;
}

export interface TwoFactorUnlockNavigateBackRouteState<T = any> {
  unlockData: {
    security: {
      token: string;
      validUntil: string;
    }
    navigateBackState?: T;
  };
}

export function createUnlockRouteState<T>(data: {
  idpId: string | null,
  email: string,
  navigateBackUrl: string;
  navigateBackState?: T
}) {
  const unlockIdp = data.idpId
    ? TwoFactorUnlockIDP.Other
    : TwoFactorUnlockIDP.Pipeliner;
  const unlockState: TwoFactorUnlockNavigateRouteState<T> = {
    unlockData: {
      idp: unlockIdp,
      email: data.email,
      navigateBackUrl: data.navigateBackUrl,
      ...(data.navigateBackState ? { navigateBackState: data.navigateBackState } : {})
    }
  };
  return unlockState;
}
